import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context";
import { useDataLayer } from "../../../../Contexts/DataLayer.context";
import ModifyHWS from "./modifyHWS.component";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";

// --- Row groupings (which fields belong to each line) ---
const ROWS = {
    height: ["height", "heightDate", "heightV"],
    weight: ["weight", "weightDate", "weightV"],
    speed: ["speed40Fastest", "speed40FDate", "speed40FVerified", "speed40FSurface"],
    note: ["hwsNote"],
};

// Reverse lookup: field -> row
const KEY_TO_ROW = Object.entries(ROWS).reduce((acc, [row, keys]) => {
    keys.forEach((k) => {
        acc[k] = row;
    });
    return acc;
}, {});

// Map each field to its controlling toggle (for your hwsData mirroring)
const TOGGLE_MAP = {
    height: "heightToggle",
    heightDate: "heightToggle",
    heightV: "heightToggle",

    weight: "weightToggle",
    weightDate: "weightToggle",
    weightV: "weightToggle",

    speed40Fastest: "speedToggle",
    speed40FDate: "speedToggle",
    speed40FVerified: "speedToggle",
    speed40FSurface: "speedToggle",

    hwsNote: "noteToggle",
};

// Build defaults for the context store (hwsData) when nothing is set yet
const buildInitialHWS = () => {
    const today = getTodayEasternYMD();
    return {
        height: "",
        heightDate: today,
        heightV: "E",
        weight: "",
        weightDate: today,
        weightV: "E",
        speed40Fastest: "",
        speed40FDate: today,
        speed40FVerified: "E",
        speed40FSurface: "F01",
        hwsNote: "",
        // Keep toggles TRUE on load as requested
        heightToggle: true,
        weightToggle: true,
        speedToggle: true,
        noteToggle: true,
        heightTouched: false,
        weightTouched: false,
        speedTouched: false,
        noteTouched: false,
    };
};

// Pull the on-screen values from the active player as-is
const pickHWS = (p = {}) => {
    const normDate = (d) => (!d || d === "0000-00-00" ? "" : d);
    return {
        // HEIGHT
        height: p.height ?? "",
        heightDate: normDate(p.heightDate),
        heightV: p.heightV ?? "",

        // WEIGHT
        weight: p.weight ?? "",
        weightDate: normDate(p.weightDate),
        weightV: p.weightV ?? "",

        // SPEED (40)
        speed40Fastest: p.speed40Fastest ?? "",
        speed40FDate: normDate(p.speed40FDate),
        speed40FVerified: p.speed40FVerified ?? "",
        speed40FSurface: p.speed40FSurface ?? "",

        // NOTE
        hwsNote: p.hwsNote ?? "",
    };
};

// Utility: did any field in a row change vs. original?
const rowChanged = (draft, original, keys) =>
    keys.some((k) => (draft[k] ?? "") !== (original[k] ?? ""));

// Utility: build a complete row object for saving
const buildRowObject = (draft, keys) =>
    keys.reduce((acc, k) => {
        acc[k] = draft[k] ?? "";
        return acc;
    }, {});

const PlayerHWS = () => {
    const { activePlayer, playerData, setPlayerData, setActivePlayer } = usePlayer();
    const { hwsData, updateHWSData } = useDataLayer();

    const [original, setOriginal] = useState(pickHWS(activePlayer));
    const [draft, setDraft] = useState(pickHWS(activePlayer));
    const [currentPlayer, setCurrentPlayer] = useState(activePlayer);

    // NEW: controls whether we show ModifyHWS (history view) or the normal HWS editor
    const [showHistory, setShowHistory] = useState(false);

    // Refs for guided tabbing (optional)
    const heightDateRef = useRef(null);
    const weightInputRef = useRef(null);
    const weightDateRef = useRef(null);
    const speedInputRef = useRef(null);
    const speedDateRef = useRef(null);
    const noteRef = useRef(null);
    const heightInputRef = useRef(null);

    // Initialize context defaults if needed; reset local snapshots when player changes
    useEffect(() => {
        const needsInit =
            !hwsData ||
            (Array.isArray(hwsData) && hwsData.length === 0) ||
            typeof hwsData !== "object";

        if (needsInit) updateHWSData(buildInitialHWS());

        setCurrentPlayer(activePlayer);
        const snap = pickHWS(activePlayer);
        setOriginal(snap);
        setDraft(snap);

        if (heightInputRef.current) {
            heightInputRef.current.focus();
            heightInputRef.current.select?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePlayer]);

    useEffect(() => {
        if (heightInputRef.current) {
            heightInputRef.current.focus();
            heightInputRef.current.select?.();
        }
    }, []);

    const today = () => getTodayEasternYMD();

    const markTouched = (row) => {
        const key = `${row}Touched`;
        if (!hwsData?.[key]) {
            updateHWSData({ ...(hwsData || {}), [key]: true });
        }
    };

    // On focus, if toggled, fill defaults from hwsData (and mirror touched)
    const forceRowDefaults = (row) => {
        const ctx = { ...(hwsData || {}) };

        if (row === "height") {
            const d = (ctx.heightDate && String(ctx.heightDate).trim()) || today();
            const v = (ctx.heightV && String(ctx.heightV).trim()) || "E";
            setDraft((prev) => ({ ...prev, heightDate: d, heightV: v }));
            updateHWSData({ ...ctx, heightDate: d, heightV: v, heightTouched: true });
        }

        if (row === "weight") {
            const d = (ctx.weightDate && String(ctx.weightDate).trim()) || today();
            const v = (ctx.weightV && String(ctx.weightV).trim()) || "E";
            setDraft((prev) => ({ ...prev, weightDate: d, weightV: v }));
            updateHWSData({ ...ctx, weightDate: d, weightV: v, weightTouched: true });
        }

        if (row === "speed") {
            const d = (ctx.speed40FDate && String(ctx.speed40FDate).trim()) || today();
            const v = (ctx.speed40FVerified && String(ctx.speed40FVerified).trim()) || "E";
            const s = (ctx.speed40FSurface && String(ctx.speed40FSurface).trim()) || "F01";
            setDraft((prev) => ({
                ...prev,
                speed40FDate: d,
                speed40FVerified: v,
                speed40FSurface: s,
            }));
            updateHWSData({
                ...ctx,
                speed40FDate: d,
                speed40FVerified: v,
                speed40FSurface: s,
                speedTouched: true,
            });
        }

        if (row === "note") {
            const n = ctx.hwsNote != null ? String(ctx.hwsNote) : "";
            setDraft((prev) => ({ ...prev, hwsNote: n }));
            updateHWSData({ ...ctx, hwsNote: n, noteTouched: true });
        }
    };

    // First input on each row: Tab behavior (optional, unchanged)
    const handleRowTab = (row, nextRowFirstRef, thisRowDateRef) => (e) => {
        if (e.key !== "Tab" || e.shiftKey) return;
        const toggleOn = !!hwsData?.[`${row}Toggle`];
        if (!toggleOn) return;

        const touched = !!hwsData?.[`${row}Touched`];
        if (!touched) {
            if (thisRowDateRef?.current) {
                e.preventDefault();
                thisRowDateRef.current.focus();
            }
        } else {
            e.preventDefault();
            forceRowDefaults(row);
            if (nextRowFirstRef?.current) nextRowFirstRef.current.focus();
        }
    };

    // Edit handler: update local draft; if row is toggled, mirror into hwsData for future auto-fill
    const onEdit = (key) => (e) => {
        const val = e?.target?.value ?? e;
        setDraft((prev) => ({ ...prev, [key]: val }));

        const toggleKey = TOGGLE_MAP[key];
        if (toggleKey && hwsData?.[toggleKey]) {
            updateHWSData({ ...(hwsData || {}), [key]: val });
        }
    };

    // --- Row-level change flags (if any field on the row changed, the whole row is "changed") ---
    const rowChangedFlags = useMemo(() => {
        return {
            height: rowChanged(draft, original, ROWS.height),
            weight: rowChanged(draft, original, ROWS.weight),
            speed: rowChanged(draft, original, ROWS.speed),
            note: rowChanged(draft, original, ROWS.note),
        };
    }, [draft, original]);

    // Is any row changed?
    const anyRowChanged = useMemo(
        () => Object.values(rowChangedFlags).some(Boolean),
        [rowChangedFlags]
    );

    // Is note filled (non-empty after trim)?
    const noteFilled = useMemo(
        () => !!String(draft?.hwsNote ?? "").trim(),
        [draft?.hwsNote]
    );

    // Can save only when something changed AND note is filled
    const canSave = useMemo(() => anyRowChanged && noteFilled, [anyRowChanged, noteFilled]);

    // overall dirty flag (any row changed)
    const isDirty = useMemo(
        () => Object.values(rowChangedFlags).some(Boolean),
        [rowChangedFlags]
    );

    // Class helper: highlight entire row if the row changed
    const cls = (key) => {
        const row = KEY_TO_ROW[key];
        const isRowChanged = row ? rowChangedFlags[row] : false;
        const base = "form-control fs-7";
        return isRowChanged ? `${base} border border-2 border-danger` : base;
    };

    // Cancel resets to original and removes highlights
    const handleCancel = () => {
        setDraft(original);
    };

    // Save: if ANY field in a row changed, save the FULL row
    const handleSave = async () => {
        if (!canSave) return;

        const payload = {};

        if (rowChangedFlags.height) {
            Object.assign(payload, buildRowObject(draft, ROWS.height));
        }

        if (rowChangedFlags.weight) {
            Object.assign(payload, buildRowObject(draft, ROWS.weight));
        }

        if (rowChangedFlags.speed) {
            Object.assign(payload, buildRowObject(draft, ROWS.speed));
        }

        if (rowChangedFlags.note) {
            Object.assign(payload, buildRowObject(draft, ROWS.note));
        }

        if (Object.keys(payload).length === 0) return;

        try {
            const response = await fetch("https://www.fbxchange.com/ReactPHP/tomBox.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    script: "insertHWSRecord",
                    FBXID: activePlayer.FBXID,
                    payload,
                }),
            });

            const data = await response.json();

            const next = { ...(activePlayer || {}), ...payload };

            Object.keys(next).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    next[key] = data[key];
                }
            });

            setActivePlayer(next);

            const updatedPlayerData = playerData.map((p) =>
                p.FBXID === next.FBXID ? { ...p, ...next } : p
            );
            setPlayerData(updatedPlayerData);

            const snap = pickHWS(next);
            setOriginal(snap);
            setDraft(snap);

            console.log("activePlayer (updated):", next);
        } catch (err) {
            console.error("Save failed:", err);
        }
    };

    // NEW: make the inner container full width when history is shown
    const contentClass = showHistory
        ? "col-48 rounded p-3 alert-light mb-3"
        : "col-40 offset-4 rounded p-3 alert-light mb-3";

    const onCloseHistory = () => {
        setShowHistory(false);
    };

    return (
        <div className="border border-1 border-dark rounded">
            {/* Header (HIDDEN when showHistory is true) */}
            {!showHistory && (
                <>
                    <div className="d-flex mb-2 col-48 alert-navy p-2 justify-content-start gap-4 text-uppercase">
                        <div>
                            {currentPlayer?.last}, {currentPlayer?.first}
                        </div>
                        <div>School: {currentPlayer?.school}</div>
                        <div>Pos: {currentPlayer?.genPos}</div>
                        <div>DY: {currentPlayer?.nflDraftYear}</div>
                        <div>
                            Jersey #: {currentPlayer?.jersey === "-1" ? "0" : currentPlayer?.jersey}
                        </div>
                    </div>
                    <div className="text-center p-2 mb-2 fw-bold">
                        This Area Updates College Data and Creates a New HWS Record when saved.
                    </div>
                </>
            )}

            {showHistory ? (
                <div className="col-48">
                    <ModifyHWS player={currentPlayer} onCloseHistory={onCloseHistory} />
                </div>
            ) : (
                <div className={contentClass}>
                    {/* HEIGHT row */}
                    <div className="d-flex mb-2 gap-2 align-items-center">
                        <div className="col-auto d-flex align-items-center">
                            <div className="form-check form-switch m-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="toggleHeight"
                                    checked={!!hwsData?.heightToggle}
                                    onChange={(e) =>
                                        updateHWSData({
                                            ...(hwsData || {}),
                                            heightToggle: !!e.target.checked,
                                        })
                                    }
                                    tabIndex={-1}
                                />
                            </div>
                        </div>

                        <div className="col-8">
                            <input
                                type="text"
                                className={cls("height")}
                                placeholder="Height"
                                value={draft.height ?? ""}
                                onChange={onEdit("height")}
                                onKeyDown={handleRowTab("height", weightInputRef, heightDateRef)}
                                onFocus={(e) => {
                                    e.target.select();
                                    markTouched("height");
                                }}
                                onMouseUp={(e) => e.preventDefault()}
                                ref={heightInputRef}
                            />
                        </div>

                        <div className="col-20">
                            <input
                                type="text"
                                className={cls("heightDate")}
                                placeholder="Height Date"
                                value={draft.heightDate}
                                onChange={onEdit("heightDate")}
                                onFocus={() => {
                                    if (hwsData?.heightToggle) {
                                        markTouched("height");
                                        forceRowDefaults("height");
                                    }
                                }}
                                ref={heightDateRef}
                            />
                        </div>
                        <div className="col-6">
                            <input
                                type="text"
                                className={cls("heightV")}
                                placeholder="P-V-E"
                                value={draft.heightV}
                                onChange={onEdit("heightV")}
                                onFocus={() => {
                                    if (hwsData?.heightToggle) {
                                        markTouched("height");
                                        forceRowDefaults("height");
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* WEIGHT row */}
                    <div className="d-flex mb-2 gap-2 align-items-center">
                        <div className="col-auto d-flex align-items-center">
                            <div className="form-check form-switch m-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="toggleWeight"
                                    checked={!!hwsData?.weightToggle}
                                    onChange={(e) =>
                                        updateHWSData({
                                            ...(hwsData || {}),
                                            weightToggle: !!e.target.checked,
                                        })
                                    }
                                    tabIndex={-1}
                                />
                            </div>
                        </div>

                        <div className="col-8">
                            <input
                                type="text"
                                className={cls("weight")}
                                placeholder="Weight"
                                value={draft.weight}
                                onChange={onEdit("weight")}
                                onKeyDown={handleRowTab("weight", speedInputRef, weightDateRef)}
                                ref={weightInputRef}
                                onFocus={(e) => {
                                    e.target.select();
                                    markTouched("weight");
                                }}
                            />
                        </div>
                        <div className="col-20">
                            <input
                                type="text"
                                className={cls("weightDate")}
                                placeholder="Weight Date"
                                value={draft.weightDate}
                                onChange={onEdit("weightDate")}
                                onFocus={() => {
                                    if (hwsData?.weightToggle) {
                                        markTouched("weight");
                                        forceRowDefaults("weight");
                                    }
                                }}
                                ref={weightDateRef}
                            />
                        </div>
                        <div className="col-6">
                            <input
                                type="text"
                                className={cls("weightV")}
                                placeholder="P-V-E"
                                value={draft.weightV}
                                onChange={onEdit("weightV")}
                                onFocus={() => {
                                    if (hwsData?.weightToggle) {
                                        markTouched("weight");
                                        forceRowDefaults("weight");
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* SPEED row */}
                    <div className="d-flex mb-2 gap-2 align-items-center">
                        <div className="col-auto d-flex align-items-center">
                            <div className="form-check form-switch m-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="toggleSpeed"
                                    checked={!!hwsData?.speedToggle}
                                    onChange={(e) =>
                                        updateHWSData({
                                            ...(hwsData || {}),
                                            speedToggle: !!e.target.checked,
                                        })
                                    }
                                    tabIndex={-1}
                                />
                            </div>
                        </div>

                        <div className="col-8">
                            <input
                                type="text"
                                className={cls("speed40Fastest")}
                                placeholder="Speed"
                                value={draft.speed40Fastest}
                                onChange={onEdit("speed40Fastest")}
                                onKeyDown={handleRowTab("speed", noteRef, speedDateRef)}
                                ref={speedInputRef}
                                onFocus={(e) => {
                                    e.target.select();
                                    markTouched("speed");
                                }}
                            />
                        </div>
                        <div className="col-20">
                            <input
                                type="text"
                                className={cls("speed40FDate")}
                                placeholder="Speed Date"
                                value={draft.speed40FDate}
                                onChange={onEdit("speed40FDate")}
                                onFocus={() => {
                                    if (hwsData?.speedToggle) {
                                        markTouched("speed");
                                        forceRowDefaults("speed");
                                    }
                                }}
                                ref={speedDateRef}
                            />
                        </div>
                        <div className="col-6">
                            <input
                                type="text"
                                className={cls("speed40FVerified")}
                                placeholder="P-V-E"
                                value={draft.speed40FVerified}
                                onChange={onEdit("speed40FVerified")}
                                onFocus={() => {
                                    if (hwsData?.speedToggle) {
                                        markTouched("speed");
                                        forceRowDefaults("speed");
                                    }
                                }}
                            />
                        </div>
                        <div className="flex-grow-1">
                            <input
                                type="text"
                                className={cls("speed40FSurface")}
                                placeholder="Surface"
                                value={draft.speed40FSurface}
                                onChange={onEdit("speed40FSurface")}
                                onFocus={() => {
                                    if (hwsData?.speedToggle) {
                                        markTouched("speed");
                                        forceRowDefaults("speed");
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* NOTE row */}
                    <div className="d-flex mb-2 gap-2 align-items-start">
                        <div className="col-auto d-flex align-items-center pt-1">
                            <div className="form-check form-switch m-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="toggleNote"
                                    checked={!!hwsData?.noteToggle}
                                    onChange={(e) =>
                                        updateHWSData({
                                            ...(hwsData || {}),
                                            noteToggle: !!e.target.checked,
                                        })
                                    }
                                    tabIndex={-1}
                                />
                            </div>
                        </div>

                        <div className="flex-grow-1">
                            <textarea
                                rows={3}
                                placeholder="HWS Note"
                                className={cls("hwsNote")}
                                value={draft.hwsNote ?? ""}
                                onChange={onEdit("hwsNote")}
                                onFocus={() => {
                                    if (hwsData?.noteToggle) {
                                        markTouched("note");
                                        forceRowDefaults("note");
                                    }
                                }}
                                ref={noteRef}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setShowHistory(true);
                            }}
                        >
                            History
                        </button>
                        <div className="d-flex ms-auto gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success text-white col-12"
                                style={{
                                    width: "200px",
                                    opacity: canSave ? 1 : 0.65,
                                    cursor: canSave ? "pointer" : "not-allowed",
                                }}
                                onClick={handleSave}
                                disabled={!canSave}
                                title={
                                    anyRowChanged
                                        ? noteFilled
                                            ? "Save changes"
                                            : "Enter a Note to save"
                                        : "No changes to save"
                                }
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlayerHWS;
